import * as React from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils'

interface TooltipProps {
  content: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** A single focusable element (button, link) the hint describes. */
  children: React.ReactElement
}

/**
 * The design-system hint. Replaces the native `title` attribute (lint:
 * `app/no-native-title`), which is unstyled, slow, and invisible on touch.
 * Icon-only buttons still need an `aria-label`; the tooltip is a visual aid.
 * Each tooltip carries its own provider, so it works in any subtree (tests,
 * portals) without app-level setup.
 */
export function Tooltip({ content, side = 'top', children }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              'z-50 max-w-xs rounded-md bg-foreground px-2 py-1 text-caption font-medium text-background shadow-overlay',
            )}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}
